import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/Form.jsx";

/*
 * The topbar's auto-refresh toggle and countdown, from dashboard.html.
 *
 * The countdown is display ONLY. TanStack Query's refetchInterval owns the
 * actual refreshing; this just tells you when the next one is due, which is the
 * whole reason the markup shows it — a number that silently changes is
 * unsettling if you cannot tell whether it is live.
 *
 * It resets on `lastUpdated`, so a manual Refresh restarts the clock rather than
 * leaving it counting toward a refetch that already happened.
 */
export default function AutoRefresh({ enabled, onToggle, intervalMs, lastUpdated }) {
    // Tick a clock and DERIVE the remaining time from the deadline, rather than
    // storing a countdown and resetting it whenever lastUpdated changes. Same
    // display, no state to keep in sync — and nothing to reset when a manual
    // refresh lands, because the deadline moves with it.
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!enabled) return;
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [enabled]);

    const left = Math.max(0, (lastUpdated || now) + intervalMs - now);
    const mm = Math.floor(left / 60000);
    const ss = Math.floor((left % 60000) / 1000);

    return (
        <span className="flex items-center gap-2">
            <Switch
                checked={enabled}
                onChange={(e) => onToggle(e.target.checked)}
                aria-label="Auto refresh"
            />
            <span className="hidden text-[12.5px] text-slate-500 sm:inline dark:text-slate-400">
                Auto refresh
            </span>
            {enabled && (
                <span
                    data-countdown
                    className="font-mono text-[12px] text-slate-400 tabular-nums dark:text-slate-500"
                    // Not announced: a value that changes every second would make
                    // a screen reader unusable, and it carries no new information.
                    aria-hidden="true"
                >
                    {mm}:{String(ss).padStart(2, "0")}
                </span>
            )}
        </span>
    );
}
